import {
  MODAL_INTERACTION,
  MODAL_HIDDEN,
  MODAL_DATA_LOADED,
  MODAL_ROWS_LOADED,
  LOADING,
  COLUMNS_DATA_LOADED,
  EXCEPTION,
} from '../actions/constants';
import { buildDataTable } from './rows';
import { AGGREGATE } from '../util/enums/EnumLayers';
import ColumnName from '../util/ColumnName';
import Column from '../util/Column';
import AggregateFunction from '../util/AggregateFunction';
import AggregateColumn from '../util/AggregateColumn';
import AggregateOperation from '../util/AggregateOperation';
import ModalAggregate from '../components/modals/ModalAggregate';

const INITIAL_STATE = {
  selected: [], // AggregateColumn objs to break down by
  countChecked: false,
  aggFunctions: [], // AggregateFunction objects to calculate
  extraColumns: [], // list of ColumnName objs

  //
  // the below are just functional data for populating the modal
  //
  readyForSave: false,
  selectedOp: null, // AggregateOperation
  plusClicked: false,
  moreClicked: false,
  relevantLayers: null, // list of layers above this layer
  availableColumns: null, // list of columns that are available to this modal
  pks: null, // list of ColumnName objs representing the primary keys present
  updated: false,
  awaitingResults: false,
  previewColumns: null, // list of ColumnName objects
  results: null, // DataTable representing results
  error: null, // string or null if no error
};

function interaction(state, payload) {
  switch (payload.section) {
    case 'include-col': {
      return {
        ...state,
        updated: true,
        extraColumns: [...state.extraColumns, payload.column.name()],
      };
    }
    case 'remove-col': {
      return {
        ...state,
        updated: true,
        extraColumns: state.extraColumns.filter(
          (cn) => !cn.equals(payload.columnName),
        ),
      };
    }
    case 'addColumn': {
      const newCol = AggregateColumn.fromColumn(
        payload.columnName,
        payload.extra,
      );
      return {
        ...state,
        selected: [...state.selected, newCol],
        plusClicked: false,
        updated: true,
      };
    }
    case 'removeColumn': {
      const newSelected = state.selected.filter(
        (aggCol) => !aggCol.equals(payload.aggCol),
      );
      const aliasStillExists =
        newSelected.filter(
          (aggCol) => aggCol.alias() === payload.aggCol.alias(),
        ).length > 0;
      return {
        ...state,
        selected: state.selected.filter(
          (aggCol) => !aggCol.equals(payload.aggCol),
        ),
        selectedOp: null, // reset
        updated: true,
        extraColumns: aliasStillExists
          ? state.extraColumns
          : state.extraColumns.filter(
              (cn) => cn.alias() !== payload.aggCol.alias(),
            ), // reset extraColumns for that alias
      };
    }
    case 'changeAgg': {
      const indexOfUpdated = state.selected.findIndex((aggCol) =>
        aggCol.equals(payload.aggCol),
      );
      const newAggCol = new AggregateColumn(
        payload.aggCol.alias(),
        payload.aggCol.name(),
        payload.aggregation,
      );
      return {
        ...state,
        selected: [
          ...state.selected.slice(0, indexOfUpdated),
          newAggCol,
          ...state.selected.slice(indexOfUpdated + 1),
        ],
        updated: true,
      };
    }
    case 'plusClicked':
      return { ...state, plusClicked: true };
    case 'moreClicked':
      return { ...state, moreClicked: true };
    case 'undoMore':
      return { ...state, selectedOp: null, moreClicked: false };
    case 'countToggled':
      return {
        ...state,
        countChecked: !state.countChecked,
        moreClicked: false,
        updated: true,
      };
    case 'chooseOp':
      return { ...state, selectedOp: payload.aggregateOperation };
    case 'removeAgg':
      return {
        ...state,
        aggFunctions: state.aggFunctions.filter(
          (ag) => ag !== payload.aggregateFunction,
        ),
        updated: true,
      };
    case 'functionArg':
      return {
        ...state,
        selectedOp: null, // reset
        moreClicked: false,
        updated: true,
        aggFunctions: [
          ...state.aggFunctions,
          new AggregateFunction(state.selectedOp, payload.columnName),
        ],
      };
    case 'addCount': {
      return {
        ...state,
        moreClicked: false,
        updated: true,
        aggFunctions: [
          ...state.aggFunctions,
          new AggregateFunction(
            new AggregateOperation('count', true),
            payload.columnName,
          ),
        ],
      };
    }
    default:
      return state;
  }
}

const modalAggregate = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case EXCEPTION: {
      if (action.awaiting === MODAL_ROWS_LOADED) {
        return { ...state, error: action.userMsg };
      }
      return state;
    }
    case LOADING: {
      if (action.awaiting === MODAL_ROWS_LOADED) {
        return {
          ...state,
          error: null,
          updated: false, // reset
          previewColumns: null, // null
          results: null, // reset
          awaitingResults: state.updated,
        };
      }
      return state;
    }
    case COLUMNS_DATA_LOADED: {
      if (!state.awaitingResults) {
        return state;
      }
      return {
        ...state,
        previewColumns: action.payload.json.map((c) => Column.build(c).name()),
      };
    }
    case MODAL_ROWS_LOADED: {
      if (!state.awaitingResults) {
        return state;
      }
      return {
        ...state,
        results: buildDataTable(action.payload._dataTable),
        awaitingResults: false,
      };
    }
    case MODAL_INTERACTION: {
      if (action.payload.modalType !== AGGREGATE) {
        return state;
      }
      const newState = interaction(state, action.payload.props);
      return {
        ...newState,
        readyForSave:
          newState.selected.length > 0 ||
          newState.countChecked ||
          newState.aggFunctions.length > 0,
      };
    }
    case MODAL_HIDDEN:
      return INITIAL_STATE; // reset modal
    case MODAL_DATA_LOADED: {
      if (action.payload.modal !== ModalAggregate) {
        return state;
      }
      let newState = state;
      if (action.payload.existingLayer) {
        // populate with existing layer
        newState = {
          ...INITIAL_STATE,
          selected: action.payload.existingLayer.keys(),
          countChecked: action.payload.existingLayer.count(),
          aggFunctions: action.payload.existingLayer.functions(),
          extraColumns: action.payload.existingLayer.extraColumns(),
          readyForSave: true,
        };
      } else if (action.payload.relevantColumnName) {
        // populate with column chosen by user
        newState = {
          ...INITIAL_STATE,
          countChecked: true,
          readyForSave: true,
          selected: [
            AggregateColumn.fromColumn(
              action.payload.relevantColumnName,
              action.payload.additionalOptions.aggregation,
            ),
          ],
        };
      } else if (action.payload.additionalOptions) {
        // user wants specific operation selected
        newState = {
          ...INITIAL_STATE,
          aggFunctions: [
            new AggregateFunction(
              action.payload.additionalOptions.selectedOp,
              action.payload.additionalOptions.columnName,
            ),
          ],
          countChecked: false,
          readyForSave: true,
        };
      }

      const allColumns = action.payload.json.map((c) => Column.build(c));
      const pks = action.payload.pks.map((pk) => ColumnName.build(pk));
      const visibleColumns = [
        ...action.payload.selectedColumns.map((cn) => ColumnName.build(cn)),
        ...pks, // also include primary keys, so user can group by entity
      ];
      const availableColumns = allColumns.filter((c) =>
        visibleColumns.some((cn) => cn.equals(c.name())),
      );
      return {
        ...newState,
        availableColumns,
        selected: newState.selected.filter(
          (
            ac, // only include columns that are actually available
          ) => availableColumns.find((c) => c.name().equals(ac.columnName())),
        ),
        updated: true,
        relevantLayers: action.payload.relevantLayers,
        pks,
      };
    }
    default:
      return state;
  }
};

export default modalAggregate;
